import AudioEngine from '@/webgl/audio/AudioEngine'
import Sound from '@/webgl/audio/Sound'
import Delay from '@/webgl/core/Delay'
import Signal from '@/webgl/core/Signal'
import GameWorld from '@/webgl/runner/GameWorld'
import { IRunnerRenderer } from '@/webgl/runner/interfaces/IRunnerRenderer'
import { getTurningPoint } from '@/webgl/runner/scenarios'
import { ScenarioSetup } from '@/webgl/runner/scenarios/ScenarioSetup.types'
import { IGameSegment } from '@/webgl/runner/systems/SequencingSystem'

export type TurningPointOptions = {
  id: string
  duration?: number
  fillAmount?: number
  decreaseAmount?: number
  audio?: string[]
}

export class TurningPoint implements IGameSegment {
  onComplete = new Signal<void>()

  queue: number = -1
  activeSound: Sound | null

  world: GameWorld
  options: TurningPointOptions
  renderer: IRunnerRenderer

  constructor(readonly id: string) {}

  init(world: GameWorld, renderer: IRunnerRenderer, setup: ScenarioSetup) {
    this.world = world
    this.renderer = renderer
    this.options = getTurningPoint(setup, this.id)
  }

  start(): void {
    if (this.options.fillAmount) this.world.state.alterJauge(this.options.fillAmount)
    if (this.options.decreaseAmount) this.world.state.alterJauge(-this.options.decreaseAmount)

    this.world.state.setTurningPoint(this.options.id)

    if (this.options.audio) {
      this.queue = -1
      this.playNextAudio()
    } else {
      Delay(this.options.duration ? this.options.duration * 1000 : 1500).then(() => {
        this.onComplete.emit(null)
      })
    }
  }

  playNextAudio = () => {
    this.queue++

    if (!AudioEngine.instance.ready || this.queue == this.options.audio.length) {
      this.activeSound = null
      this.onComplete.emit()
      return
    }

    this.activeSound = AudioEngine.play(this.options.audio[this.queue], { delay: this.queue == 0 ? 0 : 0.5 })

    // AudioEngine.play returns null when the sound is unavailable (e.g. the
    // audio library was torn down while navigating away). Bail out gracefully
    // instead of dereferencing a null sound.
    if (!this.activeSound) {
      this.onComplete.emit()
      return
    }

    this.activeSound.onEnded.on(this.playNextAudio)
  }

  stop() {
    this.activeSound?.stop()
    this.world.state.setTurningPoint(null)
  }
}
